import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AltalabLogo } from "../components/AltalabLogo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  ShoppingBag,
  Star,
  Shield,
  Clock,
  ArrowLeft,
  Loader2,
  Copy,
  Check,
  Upload,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    telegram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    privacy: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
    ),
    onlyfans: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z"/>
      </svg>
    ),
    instagram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    twitter: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    tiktok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
    closefans: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z"/>
      </svg>
    ),
  };
  return icons[platform] || <ShoppingBag className={className} />;
};

const PLATFORM_NAMES = {
  telegram: "Telegram",
  privacy: "Privacy",
  onlyfans: "OnlyFans",
  instagram: "Instagram",
  twitter: "Twitter/X",
  tiktok: "TikTok",
  closefans: "CloseFans",
};

const SellerPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixInfo, setPixInfo] = useState(null);
  const [orderMessage, setOrderMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orderStep, setOrderStep] = useState("details"); // details, pix, proof, done

  useEffect(() => {
    fetchSeller();
  }, [userId]);

  const fetchSeller = async () => {
    try {
      const response = await axios.get(`${API}/sellers/${userId}/public`);
      setSeller(response.data);
    } catch (error) {
      toast.error("Vendedor não encontrado");
    } finally {
      setLoading(false);
    }
  };

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const handleBuyClick = async (service) => {
    if (!user) {
      navigate(`/auth?redirect=/seller/${userId}`);
      return;
    }
    if (user.id === userId) {
      toast.error("Você não pode comprar seu próprio serviço");
      return;
    }
    setSelectedService(service);
    setOrderStep("details");
    setShowPixModal(true);
  };

  const handleCreateOrder = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/services/${selectedService.id}/order?Authorization=Bearer ${token}`,
        { service_id: selectedService.id, message: orderMessage }
      );
      // Fetch PIX info
      const pixRes = await axios.get(
        `${API}/sellers/${userId}/pix?Authorization=Bearer ${token}`
      );
      setPixInfo(pixRes.data);
      setOrderStep("pix");
      // Store order id for proof upload
      setSelectedService((prev) => ({ ...prev, order_id: res.data.id }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPix = async () => {
    if (pixInfo?.pix_key) {
      await navigator.clipboard.writeText(pixInfo.pix_key);
      setCopied(true);
      toast.success("Chave PIX copiada!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProofFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProofPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUploadProof = async () => {
    if (!proofFile || !selectedService?.order_id) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", proofFile);
      await axios.post(
        `${API}/orders/${selectedService.order_id}/payment-proof?Authorization=Bearer ${token}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setOrderStep("done");
      toast.success("Comprovante enviado com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar comprovante");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowPixModal(false);
    setSelectedService(null);
    setPixInfo(null);
    setOrderMessage("");
    setProofFile(null);
    setProofPreview(null);
    setOrderStep("details");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-[#080808] text-white flex flex-col items-center justify-center p-4">
        <Users className="w-16 h-16 text-white/20 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Vendedor não encontrado</h1>
        <Button onClick={() => navigate("/")} data-testid="back-to-home-btn">
          Voltar ao início
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white/70"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AltalabLogo size="small" />
          </div>
        </div>
      </header>

      <main className="pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Seller Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-white/[0.03] border border-white/10 rounded-2xl"
          >
            <div className="flex items-start gap-5">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-white/10 flex-shrink-0">
                {getPhoto(seller.profile_photo) ? (
                  <img
                    src={getPhoto(seller.profile_photo)}
                    alt={seller.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <Users className="w-12 h-12" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-2xl font-bold" data-testid="seller-name">
                    {seller.display_name}
                  </h1>
                  {seller.is_verified && (
                    <Shield className="w-5 h-5 text-emerald-400" />
                  )}
                </div>
                <p className="text-white/50 text-sm mb-3">
                  @{seller.username}
                </p>
                {seller.bio && (
                  <p className="text-white/70 text-sm mb-3">{seller.bio}</p>
                )}
                {/* Stats */}
                <div className="flex gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-white">
                      {seller.total_services}
                    </div>
                    <div className="text-white/40">Pacotes</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-white">
                      {seller.total_orders}
                    </div>
                    <div className="text-white/40">Vendas</div>
                  </div>
                  {seller.avg_rating > 0 && (
                    <div className="text-center">
                      <div className="font-bold text-yellow-400 flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        {seller.avg_rating}
                      </div>
                      <div className="text-white/40">Nota</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Platforms */}
            {seller.platforms?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {seller.platforms.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm"
                  >
                    <PlatformIcon
                      platform={p.platform}
                      className="w-4 h-4 text-[#FD5564]"
                    />
                    <span className="text-white/70">
                      {PLATFORM_NAMES[p.platform] || p.platform}
                    </span>
                    {p.reach && (
                      <span className="text-white/40">{p.reach}k</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Services List */}
          <div className="mt-8">
            <h2 className="text-lg font-semibold mb-4" data-testid="services-heading">
              Pacotes de Divulgação ({seller.services?.length || 0})
            </h2>
            <div className="space-y-4">
              {seller.services?.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-5 bg-white/[0.03] border border-white/10 rounded-2xl hover:border-[#FD5564]/30 transition-all"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {/* Show multiple platforms */}
                        {(service.platforms || [service.platform]).filter(Boolean).map((plat, pi) => (
                          <div key={pi} className="flex items-center gap-1">
                            <div className="p-1 rounded-md bg-[#FD5564]/20 text-[#FD5564]">
                              <PlatformIcon platform={plat} className="w-3.5 h-3.5" />
                            </div>
                            <Badge variant="outline" className="text-white/60 border-white/15 text-xs uppercase">
                              {PLATFORM_NAMES[plat] || plat}
                            </Badge>
                          </div>
                        ))}
                      </div>
                      <h3 className="font-semibold text-lg mb-1">
                        {service.title}
                      </h3>
                      {/* Includes tags */}
                      {service.includes?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {service.includes.map((inc, ii) => (
                            <span key={ii} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-full text-xs text-white/50">
                              {inc}
                            </span>
                          ))}
                        </div>
                      )}
                      {service.description && !service.includes?.length && (
                        <p className="text-white/50 text-sm mb-3">
                          {service.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-white/40">
                        {service.reach && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {service.reach}
                          </span>
                        )}
                        {service.delivery_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {service.delivery_time}
                          </span>
                        )}
                        {service.total_orders > 0 && (
                          <span>{service.total_orders} vendas</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4 flex flex-col items-end gap-2">
                      <div className="text-2xl font-bold text-[#FD5564]">
                        R${" "}
                        {service.price?.toFixed(2).replace(".", ",")}
                      </div>
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                        onClick={() => handleBuyClick(service)}
                        data-testid={`buy-btn-${service.id}`}
                      >
                        <ShoppingBag className="w-4 h-4 mr-1" />
                        Comprar
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
              {(!seller.services || seller.services.length === 0) && (
                <div className="text-center py-12 text-white/40">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Este vendedor ainda não tem pacotes disponíveis.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* PIX Purchase Modal */}
      <Dialog open={showPixModal} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {orderStep === "details" && "Confirmar Compra"}
              {orderStep === "pix" && "Pagamento via PIX"}
              {orderStep === "proof" && "Enviar Comprovante"}
              {orderStep === "done" && "Pedido Enviado!"}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {orderStep === "details" &&
                "Confirme os detalhes antes de pagar"}
              {orderStep === "pix" &&
                "Faça o PIX e depois envie o comprovante"}
              {orderStep === "proof" && "Anexe o comprovante de pagamento"}
              {orderStep === "done" &&
                "O vendedor verificará seu comprovante"}
            </DialogDescription>
          </DialogHeader>

          {/* Step: Details */}
          {orderStep === "details" && selectedService && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PlatformIcon
                    platform={selectedService.platform}
                    className="w-4 h-4 text-[#FD5564]"
                  />
                  <span className="text-sm text-white/60 uppercase">
                    {PLATFORM_NAMES[selectedService.platform]}
                  </span>
                </div>
                <div className="font-medium mb-1">{selectedService.title}</div>
                <div className="text-2xl font-bold text-[#FD5564]">
                  R${" "}
                  {selectedService.price?.toFixed(2).replace(".", ",")}
                </div>
              </div>
              <Textarea
                placeholder="Deixe uma mensagem para o vendedor (opcional). Ex: Link do seu perfil para divulgação..."
                value={orderMessage}
                onChange={(e) => setOrderMessage(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[80px]"
                data-testid="order-message-input"
              />
              <Button
                className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
                onClick={handleCreateOrder}
                disabled={submitting}
                data-testid="confirm-order-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Confirmar e Ver PIX"
                )}
              </Button>
            </div>
          )}

          {/* Step: PIX */}
          {orderStep === "pix" && pixInfo && (
            <div className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
                <div className="text-sm text-emerald-400 mb-1 font-medium">
                  Chave PIX ({pixInfo.pix_type?.toUpperCase()})
                </div>
                <div className="flex items-center gap-2">
                  <code
                    className="flex-1 text-lg font-mono text-white bg-black/30 px-3 py-2 rounded-lg break-all"
                    data-testid="pix-key-display"
                  >
                    {pixInfo.pix_key}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCopyPix}
                    className="text-emerald-400 hover:text-emerald-300 shrink-0"
                    data-testid="copy-pix-btn"
                  >
                    {copied ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <div className="text-sm text-white/50 mt-2">
                  Titular: {pixInfo.pix_holder_name}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-white/50 text-sm">Valor a transferir</div>
                <div className="text-2xl font-bold text-[#FD5564]">
                  R${" "}
                  {selectedService?.price?.toFixed(2).replace(".", ",")}
                </div>
              </div>
              <p className="text-sm text-white/40">
                Realize o PIX acima e depois clique em "Enviar Comprovante" para
                anexar o comprovante.
              </p>
              <Button
                className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
                onClick={() => setOrderStep("proof")}
                data-testid="go-to-proof-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Enviar Comprovante
              </Button>
            </div>
          )}

          {/* Step: Upload Proof */}
          {orderStep === "proof" && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center cursor-pointer hover:border-[#FD5564]/50 transition-colors"
                onClick={() =>
                  document.getElementById("proof-file-input")?.click()
                }
                data-testid="proof-upload-area"
              >
                {proofPreview ? (
                  <div className="relative">
                    <img
                      src={proofPreview}
                      alt="Comprovante"
                      className="max-h-48 mx-auto rounded-lg"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setProofFile(null);
                        setProofPreview(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 text-white/30 mx-auto mb-2" />
                    <p className="text-white/50 text-sm">
                      Clique para anexar o comprovante
                    </p>
                    <p className="text-white/30 text-xs mt-1">
                      PNG, JPG ou PDF
                    </p>
                  </div>
                )}
              </div>
              <input
                id="proof-file-input"
                type="file"
                accept="image/*,.pdf"
                onChange={handleProofFileChange}
                className="hidden"
              />
              <Button
                className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
                onClick={handleUploadProof}
                disabled={!proofFile || submitting}
                data-testid="upload-proof-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Enviar Comprovante"
                )}
              </Button>
            </div>
          )}

          {/* Step: Done */}
          {orderStep === "done" && (
            <div className="text-center space-y-4 py-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Comprovante Enviado!</h3>
                <p className="text-white/50 text-sm mt-1">
                  O vendedor irá verificar e confirmar o pagamento. Você receberá
                  uma notificação quando a divulgação for entregue.
                </p>
              </div>
              <Button
                className="w-full"
                variant="outline"
                onClick={closeModal}
                data-testid="close-done-btn"
              >
                Fechar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellerPage;
